import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import {
  getAssignableOrgRoleKeys,
  matchesOrgUserSearch,
  OrgUserList,
  OrgUserSearch,
  type OrgUserRecord,
} from "@doculegis/shared";
import { GlassCard } from "@/components/common/GlassCard";
import { PageMobileActions } from "@/components/common/PageMobileActions";
import { Button } from "@/components/ui/button";
import { CreateOrgUserSheet } from "@/components/users/CreateOrgUserSheet";
import { useAuth } from "@/contexts/AuthContext";
import { pageActionButtonClassName, pageActionDesktopOnlyClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { createOrgMember, listOrgMembers } from "@/lib/auth-client";

function UserManageLink({
  to,
  className,
  children,
  "aria-label": ariaLabel,
}: {
  to: string;
  className?: string;
  "aria-label"?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={className} aria-label={ariaLabel}>
      {children}
    </Link>
  );
}

export function OrgUsersPage() {
  const navigate = useNavigate();
  const { activeOrganization, syncSession } = useAuth();
  const [members, setMembers] = useState<OrgUserRecord[]>([]);
  const [memberLimit, setMemberLimit] = useState(100);
  const [memberCount, setMemberCount] = useState(0);
  const [assignableRoles, setAssignableRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listOrgMembers();
      setMemberLimit(data.memberLimit);
      setMemberCount(data.memberCount);
      setAssignableRoles(getAssignableOrgRoleKeys(data.enabledOrgRoles));
      setMembers(
        data.members.map((m) => ({
          id: m.id,
          userId: m.userId,
          role: m.role,
          createdAt: String(m.createdAt ?? ""),
          user: {
            id: m.user.id,
            email: m.user.email,
            name: m.user.name,
            fullName: m.user.fullName,
            emailVerified: m.user.emailVerified,
            banned: m.user.banned,
          },
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load members");
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredMembers = useMemo(
    () => members.filter((member) => matchesOrgUserSearch(member, searchQuery)),
    [members, searchQuery]
  );

  async function handleCreate(values: {
    email: string;
    password: string;
    name: string;
    role: string;
  }) {
    setError(null);
    setIsSubmitting(true);
    try {
      await createOrgMember({
        email: values.email,
        password: values.password,
        name: values.name || values.email.split("@")[0],
        role: values.role,
      });
      await syncSession();
      setSheetOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="p-6 pb-24 md:p-8 sm:pb-8">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] text-[#7c3aed] sm:h-10 sm:w-10 dark:border-violet-300/30 dark:bg-violet-500/15 dark:text-violet-200">
            <Users className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">Users</h1>
            <p className="hidden text-sm text-[var(--lm-muted)] sm:block dark:text-slate-300/85">
              Manage users for {activeOrganization?.name ?? "your organization"}.
            </p>
          </div>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto sm:gap-3">
          {!isLoading ? (
            <OrgUserSearch
              value={searchQuery}
              onChange={setSearchQuery}
              className="min-w-0 w-auto flex-1 sm:flex-none"
            />
          ) : null}
          <Button
            type="button"
            onClick={() => setSheetOpen(true)}
            title="Create user"
            aria-label="Create user"
            className={`${pageActionButtonClassName} ${pageActionDesktopOnlyClassName}`}
          >
            <Plus className="h-4 w-4" />
            <span className={pageActionLabelClassName}>Create user</span>
          </Button>
        </div>
      </div>

      <PageMobileActions
        actions={[
          {
            id: "create-user",
            icon: Plus,
            label: "Create user",
            onClick: () => setSheetOpen(true),
          },
        ]}
      />

      {error && !sheetOpen ? (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/15 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      ) : null}

      <GlassCard className="border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-indigo-300/25 dark:bg-slate-950/45 dark:shadow-none">
        {isLoading ? (
          <p className="text-sm text-slate-400">Loading users…</p>
        ) : (
          <OrgUserList
            members={filteredMembers}
            detailPath={(member) => `/users/${member.userId}`}
            linkComponent={UserManageLink}
            onEdit={(member) => navigate(`/users/${member.userId}`)}
            emptyMessage={searchQuery.trim() ? "No users match your search." : "No users found."}
          />
        )}
      </GlassCard>

      <CreateOrgUserSheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setError(null);
        }}
        orgName={activeOrganization?.name ?? "your organization"}
        orgRoles={assignableRoles}
        memberLimit={memberLimit}
        memberCount={memberCount}
        isSubmitting={isSubmitting}
        error={sheetOpen ? error : null}
        onSubmit={handleCreate}
      />
    </section>
  );
}
